import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from '@stencil/core';
import { TCountryRecommender } from '../../entities/content';
import { getFlagURL } from '../../services/content-service';
import globalDataLayerService from '../../services/analytics/global-data-layer-service';
import { navStateStore } from '../../state/nav-state-store';
import { ClickElementTypes, EventActions } from '../../utility/constants';
import { getConfig, getHTMLElement, isString } from '../../utility/helper';

@Component({
  tag: 'phn-country-recommender',
  styleUrl: 'country-recommender.scss',
  scoped: true
})
export class CountryRecommender {
  @Prop() locale!: string;
  @Prop() content!: TCountryRecommender;

  @State() flagURL = '';

  @Element() el!: HTMLPhnCountryRecommenderElement;

  @Event() bannerClosed!: EventEmitter<void>;

  @Watch('locale')
  async localeWatcher(): Promise<void> {
    await this.setFlagURL();
  }

  private async setFlagURL() {
    const footerAssetsUrl = getConfig(navStateStore.state.env).FOOTER_ASSETS_URL;
    this.flagURL = await getFlagURL(footerAssetsUrl, this.locale);
  }

  private closeCountryRecommender(): void {
    globalDataLayerService.pushDataLayerEvent(EventActions.COUNTRY_RECOMMENDER_CLOSE, {
      componentClick: {
        clickElementId: 'mainmenu.banner.close',
        clickElementName: 'close',
        clickElementType: ClickElementTypes.Button
      }
    });

    this.el.classList.remove('showing');
    this.el.classList.add('hiding');
  }

  componentWillLoad(): void {
    this.setFlagURL();
  }

  componentDidLoad(): void {
    globalDataLayerService.pushDataLayerEvent(EventActions.COUNTRY_RECOMMENDER_LOAD, {
      componentClick: {
        clickElementId: 'mainmenu.banner',
        clickElementName: 'Country Recommender',
        clickElementType: ClickElementTypes.Link
      }
    });
  }

  @Listen('animationend', { passive: true })
  handleAnimationEnd(e: AnimationEvent): void {
    if (e.animationName === 'slideUp') {
      getHTMLElement(e.target)?.classList.remove('hiding');
      this.bannerClosed.emit();
    } else if (e.animationName === 'slideDown') {
      getHTMLElement(e.target)?.classList.remove('showing');
    }
  }

  private sendDataLayerEvent() {
    globalDataLayerService.pushDataLayerEvent(EventActions.COUNTRY_RECOMMENDER_CHANGE, {
      componentClick: {
        clickElementId: this.content.change.id,
        clickElementName: this.content.change.text,
        clickElementType: ClickElementTypes.Link,
        targetUrl: getConfig(navStateStore.state.env).COUNTRY_SELECTOR_URL
      }
    });
  }

  render(): HTMLPhnCountryRecommenderElement {
    const { statement, region, language, change } = this.content;

    return (
      <Host class="showing">
        <div class="country-recommender-container">
          <div class="country-recommender">
            <div class="country-recommender-icons-tray">
              <div class="left-icons">
                {isString(statement) ? (
                  <phn-p-text theme="dark" size="inherit" class="statement">
                    {statement}
                  </phn-p-text>
                ) : /* istanbul ignore next */
                null}

                <div class="selector">
                  <div class="phn-flex-row phn-flex-wrap">
                    <div class="flag-region">
                      {isString(this.flagURL) ? (
                        <phn-p-icon
                          source={this.flagURL}
                          class="flag"
                          aria-hidden="true"
                          size="inherit"
                          color="inherit"
                        />
                      ) : /* istanbul ignore next */
                      null}

                      {isString(region) && isString(language) ? (
                        <phn-p-text theme="dark" class="language" size="inherit">
                          {region} / {language}
                        </phn-p-text>
                      ) : /* istanbul ignore next */
                      null}
                    </div>

                    <phn-p-link-pure
                      theme="dark"
                      size="inherit"
                      class="change"
                      onClick={() => this.sendDataLayerEvent()}
                      aria={{ 'aria-label': 'Change Region or Language' }}
                    >
                      <a href={getConfig(navStateStore.state.env).COUNTRY_SELECTOR_URL}>{change.text}</a>
                    </phn-p-link-pure>
                  </div>
                </div>
              </div>

              <div class="right-icons">
                <phn-p-button-pure
                  theme="dark"
                  icon="close"
                  onClick={() => this.closeCountryRecommender()}
                  class="close-button"
                  hide-label="true"
                >
                  Close dialog
                </phn-p-button-pure>
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
